import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { MdEdit } from 'react-icons/md';
import { SearchableSelect } from './SearchableSelect';
import { useGetProductCategoriesQuery } from '../../redux/productCategoryApi';
import {
  useCreateProductSubcategoryMutation,
  useGetProductSubcategoriesQuery,
  useUpdateProductSubcategoryMutation,
  useUpdateProductSubcategoryStatusMutation,
} from '../../redux/productSubcategoryApi';
import { CATEGORY_NAME_MAX_LEN, validateSubCategoryName } from '../../utils/validation';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';

const STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

function displayValue(value) {
  return value === '' || value == null ? '—' : String(value);
}

function AdminProductSubCategory() {
  const { canAction } = useAuth();
  const canCreate = canAction('product_category', 'create');
  const canEdit = canAction('product_category', 'edit');
  const canDelete = canAction('product_category', 'delete');
  const hasActions = canEdit || canDelete;
  const tableColSpan = hasActions ? 5 : 4;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const pageSize = DEFAULT_PAGE_SIZE;

  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filterStatus === 'active') params.status = true;
    else if (filterStatus === 'inactive') params.status = false;
    return params;
  }, [page, pageSize, search, filterStatus]);

  const { data: categoriesRes } = useGetProductCategoriesQuery({
    per_page: 500,
    status: true,
  });
  const { data: subRes, isFetching, isLoading, error: fetchError, refetch } =
    useGetProductSubcategoriesQuery(listQuery);
  const [createProductSubcategory, { isLoading: isCreating }] =
    useCreateProductSubcategoryMutation();
  const [updateProductSubcategory, { isLoading: isUpdating }] =
    useUpdateProductSubcategoryMutation();
  const [updateProductSubcategoryStatus, { isLoading: isUpdatingStatus }] =
    useUpdateProductSubcategoryStatusMutation();

  const categoryNameById = useMemo(() => {
    const list = categoriesRes?.data || [];
    const map = new Map();
    list.forEach((c) => {
      const id = c?.id;
      if (id == null) return;
      map.set(String(id), c?.name ?? '');
    });
    return map;
  }, [categoriesRes]);

  const rows = useMemo(() => {
    const categories = categoriesRes?.data || [];
    const findCategoryIdByName = (name) => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return '';
      const hit = categories.find(
        (c) => String(c?.name || '').trim().toLowerCase() === n
      );
      return hit?.id ?? '';
    };

    return (subRes?.data || []).map((row) => {
      const categoryId =
        row.product_category_id ??
        row.productCategoryId ??
        row.product_category?.id ??
        row.category_id ??
        (row.product_category_name
          ? findCategoryIdByName(row.product_category_name)
          : row.category_name
            ? findCategoryIdByName(row.category_name)
            : '');

      return {
        id: row.id,
        name: row.name,
        categoryId: String(categoryId ?? ''),
        categoryName:
          row.product_category_name ||
          row.productCategoryName ||
          row.product_category?.name ||
          row.category_name ||
          row.categoryName ||
          '',
        leadTime: row.lead_time ?? row.leadTime ?? '',
        active: row.status === true,
      };
    });
  }, [subRes, categoriesRes]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newLeadTime, setNewLeadTime] = useState('');
  const [error, setError] = useState('');

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editLeadTime, setEditLeadTime] = useState('');
  const [editStatus, setEditStatus] = useState(true);
  const [editError, setEditError] = useState('');

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    subRes,
    { page, pageSize }
  );

  const categoryOptions = useMemo(
    () =>
      (categoriesRes?.data || []).map((c) => ({
        value: String(c.id),
        label: c.name ?? String(c.id),
      })),
    [categoriesRes]
  );

  const toggleActive = (r) => {
    if (isUpdatingStatus) return;
    if (!r.categoryId) {
      toast.error('Product category missing for this row.');
      return;
    }
    updateProductSubcategoryStatus({
      id: Number(r.id),
      name: r.name,
      product_category_id: Number(r.categoryId),
      status: !r.active,
      lead_time: r.leadTime === '' || r.leadTime == null ? undefined : Number(r.leadTime),
    })
      .unwrap()
      .then(() => toast.success('Product sub category status updated successfully.'))
      .catch((err) =>
        toast.error(
          err?.data?.message || 'Failed to update product sub category status.'
        )
      );
  };

  const openAdd = () => {
    setIsAddOpen(true);
    setNewName('');
    setNewCategoryId('');
    setNewLeadTime('');
    setError('');
  };

  const closeAdd = () => setIsAddOpen(false);

  const openEdit = (r) => {
    setIsEditOpen(true);
    setEditId(r.id);
    setEditName(r.name || '');
    setEditCategoryId(String(r.categoryId ?? ''));
    setEditLeadTime(r.leadTime === '' || r.leadTime == null ? '' : String(r.leadTime));
    setEditStatus(r.active ?? true);
    setEditError('');
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditId(null);
    setEditName('');
    setEditCategoryId('');
    setEditLeadTime('');
    setEditStatus(true);
    setEditError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newCategoryId) {
      const msg = 'Please select a product category';
      setError(msg);
      toast.error(msg);
      return;
    }
    const trimmed = newName.trim();
    const validationError = validateSubCategoryName(trimmed);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = rows.some(
      (r) =>
        Number(r.categoryId) === Number(newCategoryId) &&
        r.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      const msg = 'This product sub category already exists for this category';
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      await createProductSubcategory({
        name: trimmed,
        product_category_id: Number(newCategoryId),
        status: true,
        lead_time: String(newLeadTime ?? '').trim(),
      }).unwrap();
      toast.success('Product sub category created successfully.');
      closeAdd();
    } catch (err) {
      const msg = err?.data?.message || 'Failed to create product sub category.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editCategoryId) {
      const msg = 'Please select a product category';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    const trimmed = editName.trim();
    const validationError = validateSubCategoryName(trimmed);
    if (validationError) {
      setEditError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = rows.some(
      (r) =>
        Number(r.id) !== Number(editId) &&
        Number(r.categoryId) === Number(editCategoryId) &&
        r.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      const msg = 'This product sub category already exists for this category';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    try {
      await updateProductSubcategory({
        id: Number(editId),
        name: trimmed,
        product_category_id: Number(editCategoryId),
        status: Boolean(editStatus),
        lead_time: String(editLeadTime ?? '').trim() === '' ? undefined : Number(editLeadTime),
      }).unwrap();
      toast.success('Product sub category updated successfully.');
      closeEdit();
    } catch (err) {
      const msg = err?.data?.message || 'Failed to update product sub category.';
      setEditError(msg);
      toast.error(msg);
    }
  };

  return (
    <DashboardLayout title="Product Sub Category">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-center">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search Product Subcategories"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:w-56 md:shrink-0 md:flex-row md:items-center md:gap-2">
                <span className="shrink-0 font-medium">Filter By:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="filterStatus"
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value || 'all');
                      setPage(1);
                    }}
                    placeholder="All"
                    clearable={false}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={STATUS_FILTER_OPTIONS}
                  />
                </div>
              </label>
            </div>
            {canCreate ? (
              <button
                type="button"
                className="inline-flex w-full shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
                onClick={openAdd}
              >
                + Add Product Sub Category
              </button>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Product Category Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Product Sub Category Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Lead Time
                  </th>
                  {hasActions ? (
                    <th className="border-b border-gray-200 px-3 py-2.5 text-right font-semibold text-gray-700">
                      Action
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={tableColSpan}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={fetchError}
                  refetch={refetch}
                  message="Loading product sub categories..."
                />
                {!fetchError &&
                  (rows.length > 0 || (!isLoading && !isFetching)) &&
                  rows.map((r, idx) => (
                    <tr key={r.id} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {(currentPage - 1) * pageSize + idx + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.categoryName ||
                          categoryNameById.get(String(r.categoryId)) ||
                          '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.name}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {displayValue(r.leadTime)}
                      </td>
                      {hasActions ? (
                        <td className="border-b border-gray-100 px-3 py-2.5 text-right">
                          <div className="inline-flex items-center justify-end gap-3">
                            {canEdit ? (
                              <button
                                type="button"
                                disabled={isUpdating}
                                onClick={() => openEdit(r)}
                                className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800 disabled:opacity-50"
                                aria-label="Edit product sub category"
                                title="Edit"
                              >
                                <MdEdit className="h-5 w-5 text-red-700" />
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                disabled={isUpdatingStatus}
                                onClick={() => toggleActive(r)}
                                aria-label={
                                  r.active
                                    ? 'Disable product sub category'
                                    : 'Enable product sub category'
                                }
                                className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                                  r.active
                                    ? 'border-emerald-600 bg-[#008E36]'
                                    : 'border-gray-300 bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                    r.active ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                {!fetchError && !isLoading && !isFetching && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-3 py-8 text-center text-sm text-gray-500"
                    >
                      No product sub categories found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>

        {isAddOpen ? (
          <div className="fixed inset-0 z-40 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add Product Sub Category
                </h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeAdd}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSave} className="flex flex-col gap-4">
                <label className="flex flex-row gap-1 text-sm font-medium text-gray-700">
                  Product Category <span className="text-red-600">*</span>
                </label>
                <SearchableSelect
                  name="newCategoryId"
                  value={newCategoryId}
                  onChange={(e) => {
                    setNewCategoryId(e.target.value);
                    setError('');
                  }}
                  placeholder="Select product category"
                  className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={categoryOptions}
                />
                <label className="flex flex-row gap-1 text-sm font-medium text-gray-700">
                  Product Sub Category Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  placeholder={
                    newCategoryId
                      ? 'Enter Product Sub Category Name'
                      : 'Select Product Category first'
                  }
                  value={newName}
                  maxLength={CATEGORY_NAME_MAX_LEN}
                  disabled={!newCategoryId}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setError('');
                  }}
                  className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
                />
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Lead Time (Days)</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter lead time"
                    value={newLeadTime}
                    onChange={(e) => setNewLeadTime(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                {error ? <p className="text-sm text-red-600">{error}</p> : null}
                <button
                  type="submit"
                  disabled={isCreating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60 sm:w-[140px]"
                >
                  {isCreating ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div>
          </div>
        ) : null}

        {isEditOpen ? (
          <div className="fixed inset-0 z-40 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit Product Sub Category
                </h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeEdit}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSave} className="flex flex-col gap-4">
                <label className="flex flex-row gap-1 text-sm font-medium text-gray-700">
                  Product Category <span className="text-red-600">*</span>
                </label>
                <SearchableSelect
                  name="editCategoryId"
                  value={editCategoryId}
                  onChange={(e) => {
                    setEditCategoryId(e.target.value);
                    setEditError('');
                  }}
                  placeholder="Select product category"
                  className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={categoryOptions}
                />
                <label className="flex flex-row gap-1 text-sm font-medium text-gray-700">
                  Product Sub Category Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  placeholder={
                    editCategoryId
                      ? 'Enter Product Sub Category Name'
                      : 'Select Product Category first'
                  }
                  value={editName}
                  maxLength={CATEGORY_NAME_MAX_LEN}
                  disabled={!editCategoryId}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setEditError('');
                  }}
                  className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
                />
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Lead Time (Days)</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter lead time"
                    value={editLeadTime}
                    onChange={(e) => setEditLeadTime(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </label>
                {editError ? <p className="text-sm text-red-600">{editError}</p> : null}
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60 sm:w-[140px]"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default AdminProductSubCategory;
